import React from 'react';
import styled from '@emotion/styled';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { designSystem } from '@/styles/theme';

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 30px 0 0;
  align-items: center;
  gap: 6px;

  @media (max-width: 768px) {
    gap: 6px;
    margin: 24px 0 0;
  }
`;

const IconButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #e0e0e0;
  background-color: #fff;
  border-radius: 4px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.4 : 1)};
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: #f5f5f5;
    border-color: ${designSystem.colors.primary};
  }

  svg {
    font-size: 20px;
    color: ${(props) => (props.disabled ? '#999' : '#444')};
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;

    svg {
      font-size: 20px;
    }
  }
`;

const PageButton = styled.button<{ isActive: boolean }>`
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid ${(props) => (props.isActive ? designSystem.colors.primary : '#e0e0e0')};
  background-color: ${(props) => (props.isActive ? designSystem.colors.primary : '#fff')};
  color: ${(props) => (props.isActive ? '#fff' : '#444')};
  border-radius: 4px;
  cursor: pointer;
  font-weight: ${(props) => (props.isActive ? 'bold' : 'normal')};
  transition: all 0.2s;

  &:hover {
    background-color: ${(props) => (props.isActive ? designSystem.colors.primary : '#f5f5f5')};
    border-color: ${designSystem.colors.primary};
  }

  @media (max-width: 768px) {
    min-width: 40px;
    height: 40px;
    padding: 0 8px;
    font-size: 14px;
  }
`;

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  windowSize?: number;
  onPageChange: (page: number) => void;
}

const Pagination = ({ currentPage, windowSize = 5, totalPages, onPageChange }: PaginationProps) => {
  // totalPages가 1 이하면 페이지네이션 숨김
  if (totalPages <= 1) return null;

  const handleFirstPage = () => {
    onPageChange(0);
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) onPageChange(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) onPageChange(currentPage + 1);
  };

  const handleLastPage = () => {
    onPageChange(totalPages - 1);
  };

  const renderPageNumbers = () => {
    const pages: number[] = [];
    const halfWindow = Math.floor(windowSize / 2);

    let startPage = Math.max(0, currentPage - halfWindow);
    let endPage = Math.min(totalPages - 1, currentPage + halfWindow);

    // 윈도우 크기 조정
    if (endPage - startPage < windowSize - 1) {
      if (startPage === 0) {
        endPage = Math.min(totalPages - 1, windowSize - 1);
      } else if (endPage === totalPages - 1) {
        startPage = Math.max(0, totalPages - windowSize);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages.map((pageNum) => (
      <PageButton
        key={pageNum}
        isActive={pageNum === currentPage}
        onClick={() => onPageChange(pageNum)}
      >
        {pageNum + 1}
      </PageButton>
    ));
  };

  return (
    <PaginationWrapper>
      <IconButton
        onClick={handleFirstPage}
        disabled={currentPage === 0}
      >
        <FirstPageIcon />
      </IconButton>

      <IconButton
        onClick={handlePreviousPage}
        disabled={currentPage === 0}
      >
        <ChevronLeftIcon />
      </IconButton>

      {renderPageNumbers()}

      <IconButton
        onClick={handleNextPage}
        disabled={currentPage === totalPages - 1}
      >
        <ChevronRightIcon />
      </IconButton>

      <IconButton
        onClick={handleLastPage}
        disabled={currentPage === totalPages - 1}
      >
        <LastPageIcon />
      </IconButton>
    </PaginationWrapper>
  );
};

export default Pagination;
